import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { type FileRejection, type FileWithPath, useDropzone } from 'react-dropzone';

import { useTrackViewedOnboardingUpload } from '@air/analytics';
import { UploadIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { cn } from '@air/utils-tailwind';

import { useOnboardingPreferencesStore } from '~/components/v2/store/features/onboarding/useOnboardingPreferencesStore';
import { StepHeader } from '~/components/WorkspaceOnboardingForm/StepHeader';
import { useSubmitOnboardingUpload } from '~/components/WorkspaceOnboardingForm/useSubmitOnboardingUpload';
import { getTotalFormSteps } from '~/components/WorkspaceOnboardingForm/utils';
import { WorkspaceOnboardingCardLayout } from '~/components/WorkspaceOnboardingForm/WorkspaceOnboardingCardLayout';
import { WorkspaceOnboardingContainer } from '~/components/WorkspaceOnboardingForm/WorkspaceOnboardingContainer';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useWorkspaces } from '~/swr-hooks/workspaces/useWorkspaces';

type UploadInputType = 'files' | 'folders';

export const WorkspaceOnboardingUploadPrompt = memo(() => {
  const { trackViewedOnboardingUpload } = useTrackViewedOnboardingUpload();
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;
  const { data: workspaces } = useWorkspaces();
  const [inputType, setInputType] = useState<UploadInputType | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const clearShowFirstUploadPrompt = useOnboardingPreferencesStore((state) => state.clearShowFirstUploadPrompt);
  const { submitOnboardingUpload } = useSubmitOnboardingUpload();
  const { skipOnboardingUpload } = useFeatureFlags();
  const { onboardingTeamInviteStepEnabled } = useTemporaryFeatureFlags();

  const isFirstWorkspace = workspaces && workspaces.length === 1;
  const totalSteps = getTotalFormSteps(
    !!isFirstWorkspace,
    Boolean(isFirstWorkspace && onboardingTeamInviteStepEnabled),
  );

  useEffect(() => {
    trackViewedOnboardingUpload({ category: 'onboarding' });
  }, [trackViewedOnboardingUpload]);

  const handleDrop = useCallback(
    async (acceptedFiles: FileWithPath[], fileRejections: FileRejection[]) => {
      setInputType(null);
      setIsDraggingOver(false);
      await submitOnboardingUpload({
        acceptedFiles,
        category: 'onboarding',
        fileRejections,
      });
    },
    [submitOnboardingUpload],
  );

  const handleFileDialogCancel = useCallback(() => {
    setInputType(null);
  }, []);

  const handleDragEnter = useCallback(() => {
    setIsDraggingOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDraggingOver(false);
  }, []);

  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    onDrop: handleDrop,
    onDragEnter: handleDragEnter,
    onDragLeave: handleDragLeave,
    onFileDialogCancel: handleFileDialogCancel,
    useFsAccessApi: false,
  });

  const openRef = useRef(open);
  openRef.current = open;

  useEffect(() => {
    if (inputType && openRef.current) {
      openRef.current();
    }
  }, [inputType]);

  const handleSelectFilesClick = useCallback(() => {
    setInputType('files');
  }, []);

  const handleSelectFoldersClick = useCallback(() => {
    setInputType('folders');
  }, []);

  const handleSkipClick = useCallback(() => {
    if (workspaceId) {
      clearShowFirstUploadPrompt(workspaceId);
    }
  }, [clearShowFirstUploadPrompt, workspaceId]);

  return (
    <WorkspaceOnboardingContainer>
      <WorkspaceOnboardingCardLayout currentStep={totalSteps - 1} totalSteps={totalSteps}>
        <form className="flex min-h-0 flex-1 flex-col gap-2">
          <div className="flex min-h-0 flex-1 flex-col gap-6" data-testid="WORKSPACE_ONBOARDING_UPLOAD_PROMPT">
            <StepHeader
              title="Upload your first file."
              description="Your workspace comes to life once you add an asset. Drop in a file to see what Air can do."
            />
            <div
              {...getRootProps()}
              data-testid="WORKSPACE_ONBOARDING_UPLOAD_DROPZONE"
              className={cn(
                'flex min-h-0 flex-1 flex-col items-center justify-center gap-5 rounded-12 border px-4 py-4 transition-colors',
                isDraggingOver
                  ? 'ads:border-primary border-2 border-altitude-9 border-solid ads:bg-primary-subtle bg-altitude-2'
                  : 'ads:border-ads-gray-7 border-gray-7 border-dashed ads:bg-base bg-gray-1',
              )}
            >
              {inputType === 'folders' ? (
                <input
                  {...getInputProps()}
                  // @ts-expect-error - https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/webkitdirectory - "directory" is a nonstandard prop but is the only way to do it 💁
                  directory=""
                  mozdirectory=""
                  type="file"
                  webkitdirectory=""
                  data-testid="WORKSPACE_ONBOARDING_UPLOAD_FOLDER_INPUT"
                />
              ) : (
                <input {...getInputProps()} data-testid="WORKSPACE_ONBOARDING_UPLOAD_FILE_INPUT" />
              )}
              <UploadIcon className="ads:text-icon-emphasis text-gray-12" size="md" />
              <div className="flex flex-col items-center gap-1 text-center">
                <div className="font-medium ads:text-primary text-16 text-gray-12">Drag and drop your files here</div>
                <div className="ads:text-secondary text-12 text-gray-11">Add your creative assets here</div>
              </div>

              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button appearance="filled" color="gray" type="button" size="small" onClick={handleSelectFilesClick}>
                  Select files
                </Button>
                <Button appearance="filled" color="gray" type="button" size="small" onClick={handleSelectFoldersClick}>
                  Select folders
                </Button>
              </div>
            </div>
          </div>
        </form>
        {skipOnboardingUpload ? (
          <div className="flex justify-end">
            <Button
              appearance="ghost"
              color="gray"
              type="button"
              size="medium"
              onClick={handleSkipClick}
              data-testid="WORKSPACE_ONBOARDING_UPLOAD_SKIP"
            >
              Skip
            </Button>
          </div>
        ) : null}
      </WorkspaceOnboardingCardLayout>
    </WorkspaceOnboardingContainer>
  );
});

WorkspaceOnboardingUploadPrompt.displayName = 'WorkspaceOnboardingUploadPrompt';
